export interface AltChunkContent {
  html: string;
  plainText: string;
}

const AFCHUNK_REL_TYPE =
  "http://schemas.openxmlformats.org/officeDocument/2006/relationships/aFChunk";

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Decode quoted-printable content from MHT parts (html-docx-js export format). */
export function decodeQuotedPrintable(input: string): string {
  const withoutSoftBreaks = input.replace(/=\r?\n/g, "");
  return withoutSoftBreaks.replace(/=([0-9A-Fa-f]{2})/g, (_, hex: string) =>
    String.fromCharCode(parseInt(hex, 16)),
  );
}

function extractHtmlBodyContent(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const bodyHtml = doc.body?.innerHTML?.trim();
  if (bodyHtml) return bodyHtml;
  return html.trim();
}

/** Parse an MHT (multipart/related) archive and return the HTML part body. */
export function parseMhtHtml(mhtContent: string): string | null {
  const boundaryMatch = mhtContent.match(/boundary="([^"]+)"/i);
  if (!boundaryMatch) return null;

  const boundary = boundaryMatch[1];
  const parts = mhtContent.split(new RegExp(`--${escapeRegExp(boundary)}`, "g"));

  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed || trimmed === "--") continue;

    const splitIndex = part.search(/\r?\n\r?\n/);
    if (splitIndex === -1) continue;

    const headers = part.slice(0, splitIndex);
    let body = part.slice(splitIndex).replace(/^\r?\n\r?\n/, "");

    if (!/content-type:\s*text\/html/i.test(headers)) continue;

    const encoding = headers
      .match(/content-transfer-encoding:\s*(\S+)/i)?.[1]
      ?.toLowerCase();
    if (encoding === "quoted-printable") {
      body = decodeQuotedPrintable(body);
    }

    const html = extractHtmlBodyContent(body);
    if (html.trim()) return html;
  }

  return null;
}

function htmlToPlainText(html: string): string {
  const doc = new DOMParser().parseFromString(
    `<div id="altchunk-root">${html}</div>`,
    "text/html",
  );
  return (doc.getElementById("altchunk-root")?.textContent ?? "")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeZipPath(target: string): string {
  let path = target.trim();
  if (path.startsWith("/")) path = path.slice(1);
  if (!path.startsWith("word/")) path = `word/${path}`;
  return path;
}

function documentHasAltChunk(documentXml: string): boolean {
  return /<w:altChunk\b/i.test(documentXml) || /<altChunk\b/i.test(documentXml);
}

function resolveAltChunkPath(documentXml: string, relsXml: string): string | null {
  if (!documentHasAltChunk(documentXml)) return null;

  const relsDoc = new DOMParser().parseFromString(relsXml, "application/xml");
  if (relsDoc.querySelector("parsererror")) return "word/afchunk.mht";

  const relationships = relsDoc.getElementsByTagName("Relationship");
  for (let i = 0; i < relationships.length; i++) {
    const rel = relationships[i];
    const type = rel.getAttribute("Type") ?? "";
    if (type === AFCHUNK_REL_TYPE || type.endsWith("/aFChunk")) {
      const target = rel.getAttribute("Target");
      if (target) return normalizeZipPath(target);
    }
  }

  return "word/afchunk.mht";
}

/**
 * Extract HTML from DOCX files that store body content as w:altChunk + MHT
 * (e.g. LexGat DOCX downloads via html-docx-js).
 */
export async function extractAltChunkContent(
  arrayBuffer: ArrayBuffer,
): Promise<AltChunkContent | null> {
  const JSZip = (await import("jszip")).default;
  const zip = await JSZip.loadAsync(arrayBuffer);

  const documentXml = await zip.file("word/document.xml")?.async("text");
  if (!documentXml || !documentHasAltChunk(documentXml)) return null;

  const relsXml = await zip.file("word/_rels/document.xml.rels")?.async("text");
  const mhtPath = relsXml
    ? resolveAltChunkPath(documentXml, relsXml)
    : "word/afchunk.mht";
  if (!mhtPath) return null;

  const mhtContent = await zip.file(mhtPath)?.async("text");
  if (!mhtContent) return null;

  const html = parseMhtHtml(mhtContent);
  if (!html?.trim()) return null;

  return {
    html,
    plainText: htmlToPlainText(html),
  };
}
